"use client"
import Link from "next/link"
import Image from "next/image"
import dynamic from "next/dynamic"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import type { GoogleReviewsData } from "@/lib/google-reviews"
import ServiceDetailsSection from "@/components/service-details-section"

// Below-the-fold sections are code-split so their client JS is not part of the
// initial critical bundle. ssr:true keeps the markup in the server-rendered HTML,
// preserving SEO and preventing layout shift / flash of unstyled content.
const GoogleReviewsSlider = dynamic(() => import("@/components/google-reviews-slider"), { ssr: true })
const BrandLogosSection = dynamic(() => import("@/components/brand-logos-section"), { ssr: true })
const PaymentMethodsSection = dynamic(() => import("@/components/payment-methods-section"), { ssr: true })

// Define the brand logos array to use in multiple places
const brandLogos = [
  { name: "Whirlpool", src: "/images/brands/whirlpool-logo.png", height: 45 },
  { name: "Maytag", src: "/images/brands/maytag-logo.png", height: 45 },
  { name: "LG", src: "/images/brands/lg-logo.png", height: 45 },
  { name: "KitchenAid", src: "/images/brands/kitchenaid-logo.png", height: 35 },
  { name: "Sub-Zero", src: "/images/brands/subzero-logo.png", height: 35 },
  { name: "Wolf", src: "/images/brands/wolf-logo.png", height: 40 },
  { name: "Thermador", src: "/images/brands/thermador-logo.jpeg", height: 40 },
  { name: "Sharp", src: "/images/brands/sharp-logo.png", height: 35 },
  { name: "Haier", src: "/images/brands/haier-logo.png", height: 45 },
  { name: "Kenmore", src: "/images/brands/kenmore-logo.svg", height: 45 },
  { name: "Beko", src: "/images/brands/beko-logo.png", height: 40 },
  { name: "Fisher & Paykel", src: "/images/brands/fisher-paykel-logo.png", height: 30 },
  { name: "Electrolux", src: "/images/brands/electrolux-logo.png", height: 40 },
  { name: "Bosch", src: "/images/brands/bosch-logo.png", height: 40 },
  { name: "Frigidaire", src: "/images/brands/frigidaire-logo.png", height: 35 },
  { name: "Gaggenau", src: "/images/brands/gaggenau-logo.png", height: 35 },
  { name: "JennAir", src: "/images/brands/jennair-logo.png", height: 35 },
  { name: "GE", src: "/images/brands/ge-logo.png", height: 50 },
  { name: "Amana", src: "/images/brands/amana-logo.png", height: 40 },
  { name: "Samsung", src: "/images/brands/samsung-logo.png", height: 40 },
  { name: "Miele", src: "/images/brands/miele-logo.png", height: 40 },
  { name: "Viking", src: "/images/brands/viking-logo.jpeg", height: 105 },
  // New brands added
  { name: "Liebherr", src: "/images/brands/liebherr-logo.png", height: 40 },
  { name: "Speed Queen", src: "/images/brands/speed-queen-logo.png", height: 40 },
]

export default function HomeClient({ reviewsData }: { reviewsData: GoogleReviewsData | null }) {
  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-red-200 via-red-100 to-red-200">
      {/* Services Overview */}
      <section className="py-10 sm:py-12 lg:py-16 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-gray-900 text-center mx-auto max-w-3xl">
              Our Repair Services
            </h2>
            <div className="w-20 h-1 bg-red-600 mx-auto mt-3 mb-4 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Refrigerator Repair",
                description: "Cooling issues, ice maker problems, water leaks",
                image: "/images/services/refrigerator-repair.png",
              },
              {
                title: "Washer & Dryer Repair",
                description: "Fixing leaks, strange noises, poor cleaning performance",
                image: "/images/services/washer-dryer-repair.png",
              },
              {
                title: "Oven & Range Repair",
                description: "Heating problems, temperature issues, burner repairs",
                image: "/images/services/oven-range-repair.png",
              },
              {
                title: "Dishwasher Repair",
                description: "Drainage problems, cleaning issues, and noise repairs",
                image: "/images/services/dishwasher-repair.png",
              },
              {
                title: "Microwave Repair",
                description: "Heating issues, turntable problems, control panel repairs",
                image: "/images/services/microwave-repair.png",
              },
              {
                title: "Other Appliances",
                description: "Garbage disposals, ice machines, wine coolers, and more",
                image: "/images/services/other-appliances-v2.png",
              },
            ].map((service, index) => (
              <Card key={index} className="overflow-hidden border-0 shadow-md h-full hover:shadow-lg transition-shadow">
                <div className="p-4 flex flex-col h-full">
                  <div className="flex justify-center service-icon">
                    <Image
                      src={service.image || "/placeholder.svg"}
                      alt={service.title}
                      width={160}
                      height={160}
                      className="h-32 w-auto object-contain"
                    />
                  </div>
                  <h3 className="mt-3 text-base font-bold text-center text-gray-900">{service.title}</h3>
                  <p className="mt-1 text-center text-gray-600 flex-grow text-sm">{service.description}</p>
                  <div className="mt-3 flex justify-center">
                    <Button
                      asChild
                      className="bg-red-600 hover:bg-white text-white hover:text-red-800 border border-red-600 hover:border-gray-300 rounded-full"
                    >
                      <Link href="/request-service">
                        <span className="font-bold">Request Service Now</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button
              asChild
              className="bg-white hover:bg-red-600 text-red-800 hover:text-white border border-gray-300 rounded-full"
            >
              <Link href="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      <ServiceDetailsSection />

      {/* Google Reviews Section */}
      <GoogleReviewsSlider initialData={reviewsData} />

      {/* Brands We Service Section */}
      <BrandLogosSection />

      {/* Payment Methods Section - Simplified */}
      <PaymentMethodsSection />
    </div>
  )
}
